import { Box } from "@rebass/grid";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import { useSelf } from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import * as contactActions from "@rentiohq/shared-frontend/dist/redux/contact/contact.actions";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import * as countActions from "@rentiohq/shared-frontend/dist/redux/count/count.actions";
import * as countSelectors from "@rentiohq/shared-frontend/dist/redux/count/count.selectors";
import { ECountIdentifier } from "@rentiohq/shared-frontend/dist/redux/count/count.types";
import * as tagHooks from "@rentiohq/shared-frontend/dist/reduxV2/tag/tag.hooks";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { append } from "@rentiohq/shared-frontend/dist/utils/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  AddContactModal,
  Card,
  EFilterType,
  EmptyState,
  ESpacings,
  Filters,
  Icon,
  Loading,
  Page,
  Pagination,
  ResourceList,
} from "@rentiohq/web-shared/dist/components";
import { ContactOverviewRow } from "@rentiohq/web-shared/dist/components/ContactOverviewRow/ContactOverviewRow";
import { TContactType } from "@rentiohq/web-shared/dist/types";
import { compact } from "lodash";
import hash from "object-hash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import {
  ArrayParam,
  BooleanParam,
  createEnumParam,
  NumberParam,
  QueryParamConfig,
  StringParam,
  withDefault,
} from "serialize-query-params";
import { useDebouncedCallback } from "use-debounce";
import useDeepCompareEffect from "use-deep-compare-effect";
import { ts } from "../../../../services";
import {
  EConnectionType,
  EDummyType,
  EPersonType,
  getFilterQuery,
} from "./ContactsOverview.utils";

export const ContactsOverview = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [contactType, setContactType] = useState<TContactType>("contact");

  const [queryParams, setQueryParamValue] = useQueryParams({
    query: withDefault(StringParam, ""),
    page: withDefault(NumberParam, 1),
    personType: createEnumParam(Object.values(EPersonType)),
    connectionType: createEnumParam(Object.values(EConnectionType)),
    dummyTypes: ArrayParam as QueryParamConfig<
      EDummyType[] | null | undefined,
      EDummyType[] | null | undefined
    >,
    bookkeeping: BooleanParam,
    tagIds: ArrayParam as QueryParamConfig<
      string[] | null | undefined,
      string[] | null | undefined
    >,
  });

  const [queryInput, setQueryInput] = useState<string>(queryParams.query || "");

  const hasDuplicateContactsForBroker = useBrokerFeature(
    EBrokerFeature.DuplicateContacts,
  );
  const hasBookkeeping = useBrokerFeature(EBrokerFeature.Bookkeeping);

  const { items: allTags = [] } = tagHooks.usePaged({
    shouldRefetch: true,
    query: { page: 1, limit: 100 },
  });

  const { broker } = useSelf();

  // Helpers
  const contactsIdentifier = hash({
    query: queryParams.query,
    personType: queryParams.personType,
    connectionType: queryParams.connectionType,
    dummyType: queryParams.dummyTypes,
    dummyEmail: broker?.dummyEmail,
    dummyPhone: broker?.dummyPhone,
    bookkeeping: queryParams.bookkeeping,
    tagIds: queryParams.tagIds,
  });
  const countIdentifier = `${ECountIdentifier.ContactsOverviewPaged}-${contactsIdentifier}`;

  const countBase = useMemo(
    () =>
      append("/contacts/count", {
        where: getFilterQuery({
          queryParams,
          dummyEmail: broker?.dummyEmail,
          dummyPhone: broker?.dummyPhone,
        }).where,
        search: getFilterQuery({
          queryParams,
          dummyEmail: broker?.dummyEmail,
          dummyPhone: broker?.dummyPhone,
        }).search,
      }),
    [
      queryParams.query,
      queryParams.personType,
      queryParams.connectionType,
      queryParams.dummyTypes,
      queryParams.bookkeeping,
      queryParams.tagIds,
      broker?.dummyEmail,
      broker?.dummyPhone,
    ],
  );

  const [setDebouncedValue, cancel] = useDebouncedCallback(
    // to memoize debouncedFunction we use useCallback hook.
    // In this case all linters work correctly
    useCallback(
      (values: { [key: string]: any }) => {
        setQueryParamValue(values);
      },
      [setQueryParamValue],
    ),
    250,
  );

  const dispatch = useDispatch();
  const contactsPagedByPage = useSelector((state: IRootStore) =>
    contactSelectors.getContactsPaged(
      state,
      contactsIdentifier,
      queryParams.page || 1,
    ),
  );
  const contactsPagedTotalCount = useSelector((state: IRootStore) =>
    countSelectors.getCount(state, countIdentifier),
  );
  const contactsPagedIsFetching = useSelector((state: IRootStore) =>
    contactSelectors.isFetchingContactsForPage(
      state,
      contactsIdentifier,
      queryParams.page || 1,
    ),
  );

  useEffect(() => cancel, [cancel]);

  useEffect(() => {
    if (queryInput === queryParams.query) {
      return;
    }
    setDebouncedValue({ query: queryInput, page: 1 });
  }, [queryInput, queryParams.query]);

  const fetchData = () => {
    dispatch(
      contactActions.getContactsPaged.actions.start({
        id: contactsIdentifier,
        page: queryParams.page || 1,
        filter: getFilterQuery({
          queryParams,
          dummyEmail: broker?.dummyEmail,
          dummyPhone: broker?.dummyPhone,
        }),
      }),
    );
  };

  useDeepCompareEffect(() => {
    fetchData();
  }, [queryParams, broker?.dummyEmail, broker?.dummyPhone]);

  useEffect(() => {
    dispatch(
      countActions.getCount.actions.start({
        countIdentifier,
        countBase,
      }),
    );
  }, [
    queryParams.query,
    queryParams.personType,
    queryParams.connectionType,
    queryParams.dummyTypes,
    queryParams.bookkeeping,
    queryParams.tagIds,
    broker?.dummyEmail,
    broker?.dummyPhone,
  ]);

  // Events
  const handleCreateContact = (contactType: TContactType) => () => {
    setModalOpen(true);
    setContactType(contactType);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalSubmit = (x: any) => {
    setModalOpen(false);
    setQueryParamValue({ page: 1, query: undefined });
    fetchData();
  };

  const handleFiltersQueryChange = useCallback((value: any) => {
    setQueryInput(value);
  }, []);

  const handleFilterQueryValueRemove = useCallback(() => setQueryInput(""), []);

  const handlePageClick = ({ selected }: { selected: number }) => {
    setQueryParamValue({ page: selected + 1 });
  };

  const handleSetPersonType = (newPersonType?: EPersonType) => {
    setQueryParamValue({ personType: newPersonType, page: 1 });
  };

  const handleSetConnectionType = (newConnectionType?: EConnectionType) => {
    setQueryParamValue({ connectionType: newConnectionType, page: 1 });
  };

  const handleSetDummyTypes = (newDummyTypes?: EDummyType[]) => {
    setQueryParamValue({ dummyTypes: newDummyTypes, page: 1 });
  };

  const handleSetBookkeeping = (newBookkeeping?: boolean) => {
    setQueryParamValue({ bookkeeping: newBookkeeping, page: 1 });
  };

  const handleSetTagIds = (newTagIds?: string[]) => {
    setQueryParamValue({ tagIds: newTagIds, page: 1 });
  };

  const initialPage = queryParams.page ? queryParams.page - 1 || 0 : 0;
  const pageCount = contactsPagedTotalCount
    ? Math.ceil(contactsPagedTotalCount / 20)
    : 1;

  // Render functions
  const renderContactItem = (item: any) => {
    return <ContactOverviewRow contact={item} clickable={true} />;
  };

  const renderContent = () => {
    if (contactsPagedIsFetching && !contactsPagedByPage) {
      return <Loading />;
    }

    if (contactsPagedByPage && contactsPagedByPage.length === 0) {
      return <EmptyState icon="contacts" />;
    }

    return (
      <ResourceList
        items={contactsPagedByPage || []}
        renderItem={renderContactItem}
      />
    );
  };

  return (
    <Page
      title={ts.contactOverviewTitle()}
      // loading={initialLoading}
      // error={error}
      actions={[
        {
          appearance: "primary",
          content: ts.addContact(),
          dropdown: [
            {
              content: ts.addContact(),
              onClick: handleCreateContact("contact"),
              media: <Icon source="add" />,
            },
            {
              content: ts.addCompany(),
              onClick: handleCreateContact("company"),
              media: <Icon source="add" />,
            },
          ],
        },
      ]}
    >
      <Card>
        <Box mb={ESpacings.loose}>
          <Filters
            queryValue={queryInput}
            queryPlaceholder={ts.contactsFilterQueryPlaceholder()}
            onQueryChange={handleFiltersQueryChange}
            onQueryClear={handleFilterQueryValueRemove}
            filterConfigs={compact([
              {
                label: getLocalizedText("contacts.filter.person_type"),
                groupKey: "person_type",
                filters: [
                  {
                    filterKey: "person_type",
                    type: EFilterType.SingleSelect,
                    options: Object.values(EPersonType),
                    values: queryParams.personType
                      ? [queryParams.personType]
                      : undefined,
                    translate: value =>
                      getLocalizedText(
                        `contacts.filter.person_type.${value}`.toLowerCase(),
                      ),
                    onChange: (value: string) => {
                      handleSetPersonType(value as EPersonType);
                    },
                    onRemove: () => {
                      handleSetPersonType(undefined);
                    },
                  },
                ],
              },
              {
                label: getLocalizedText("contacts.filter.connection_type"),
                groupKey: "connection_type",
                filters: [
                  {
                    filterKey: "connection_type",
                    type: EFilterType.SingleSelect,
                    options: Object.values(EConnectionType).filter(x => {
                      if (x === EConnectionType.DuplicateWithoutConnection) {
                        return hasDuplicateContactsForBroker;
                      }
                      return true;
                    }),
                    values: queryParams.connectionType
                      ? [queryParams.connectionType]
                      : undefined,
                    translate: value =>
                      getLocalizedText(
                        `contacts.filter.connection_type.${value}`.toLowerCase(),
                      ),
                    onChange: (value: string) => {
                      handleSetConnectionType(value as EConnectionType);
                    },
                    onRemove: () => {
                      handleSetConnectionType(undefined);
                    },
                  },
                ],
              },
              broker?.dummyEmail || broker?.dummyPhone
                ? {
                    label: getLocalizedText("contacts.filter.dummy_types"),
                    groupKey: "dummy_types",
                    filters: [
                      {
                        filterKey: "dummy_types",
                        type: EFilterType.MultiSelect,
                        options: (() => {
                          const options: EDummyType[] = [];
                          if (broker?.dummyEmail) {
                            options.push(EDummyType.Email);
                          }
                          if (broker?.dummyPhone) {
                            options.push(EDummyType.Phone);
                          }
                          return options;
                        })(),
                        values: queryParams.dummyTypes,
                        translate: value => {
                          if (value === EDummyType.Email) {
                            return broker?.dummyEmail || "";
                          }
                          if (value === EDummyType.Phone) {
                            return broker?.dummyPhone || "";
                          }
                          return "";
                        },
                        onChange: (values: string[]) => {
                          handleSetDummyTypes(values as EDummyType[]);
                        },
                        onRemove: (value: string) => {
                          handleSetDummyTypes(
                            queryParams.dummyTypes?.filter(x => x !== value),
                          );
                        },
                        appliedFiltersPrefix: getLocalizedText(
                          "contacts.filter.dummy_types.prefix",
                        ),
                      },
                    ],
                  }
                : undefined,
              hasBookkeeping
                ? {
                    label: getLocalizedText("contacts.filter.bookkeeping"),
                    groupKey: "bookkeeping",
                    filters: [
                      {
                        filterKey: "bookkeeping",
                        type: EFilterType.SingleSelect,
                        options: ["yes", "no"],
                        values:
                          queryParams.bookkeeping !== undefined
                            ? [queryParams.bookkeeping ? "yes" : "no"]
                            : undefined,
                        translate: value =>
                          getLocalizedText(
                            `contacts.filter.bookkeeping.${value}`.toLowerCase(),
                          ),
                        onChange: (values: string[]) => {
                          const value = values[0];
                          handleSetBookkeeping(
                            value ? value === "yes" : undefined,
                          );
                        },
                        onRemove: () => {
                          handleSetBookkeeping(undefined);
                        },
                        appliedFiltersPrefix: getLocalizedText(
                          "contacts.filter.bookkeeping.prefix",
                        ),
                      },
                    ],
                  }
                : undefined,
              allTags.length > 0
                ? {
                    label: getLocalizedText("system.entity.tags"),
                    groupKey: "tags",
                    filters: [
                      {
                        filterKey: "tags",
                        type: EFilterType.MultiSelect,
                        options: allTags.map(tag => String(tag.id)),
                        values: queryParams.tagIds ?? undefined,
                        translate: (value: string | object) =>
                          typeof value === "string"
                            ? getLocalizedText(
                                allTags.find(tag => String(tag.id) === value)
                                  ?.key ?? value,
                              )
                            : "",
                        onChange: (values: string[]) => {
                          handleSetTagIds(
                            values.length > 0 ? values : undefined,
                          );
                        },
                        onRemove: (value: string) => {
                          const next = (queryParams.tagIds ?? []).filter(
                            id => id !== value,
                          );
                          handleSetTagIds(next.length > 0 ? next : undefined);
                        },
                      },
                    ],
                  }
                : undefined,
            ])}
          />
        </Box>
        {renderContent()}
        {!!contactsPagedTotalCount && pageCount > 1 && (
          <Pagination
            initialPage={initialPage}
            pageCount={pageCount}
            onPageChange={handlePageClick}
          />
        )}
      </Card>
      {modalOpen && (
        <AddContactModal
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          type={contactType}
        />
      )}
    </Page>
  );
};
